import { localizedStrings } from '@core/strings'
import { statuses } from '@core/theme/statuses'
import React, { useMemo } from 'react'
import { Box, Flex, Text } from 'webapp/components/common/fundamentals'
import { useModal } from 'webapp/components/common/GlobalModal'
import { Button, Card, Chip, Divider } from '@common/material'
import { useGetPermitTemplateRolesByProject } from '@core/react-query/features/templates/permitTemplates'
import { PermitTemplateRole } from '@core/entities/permit/PermitTemplateRoles'
import { PermitDetailsSidebarProps } from './types'
import { CardContent } from '@mui/material'
import { useGetProjectPlan } from '@core/react-query/features/projects/plans'
import { Collapsible } from '@common/Collapsible'
import { Section } from '@common/Section'
import moment from 'moment'
import { ProjectPlanPermitsModal } from '../projectPlan/modals/ProjectPlanPermitsModal'
import { PermitApprovalWrapper } from '@core/entities/permit/PermitApprovalWrapper'
import LaunchIcon from '@mui/icons-material/Launch'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { PageUrls } from '@core/page-urls'
import { ReviewPermitCard } from './permitDetailsHeader/ReviewPermitCard'
import { RequestedChangesCard } from './permitDetailsHeader/RequestedChangesCard'
import { TransferPermitCard } from './permitDetailsHeader/TransferPermitCard'
import { ReceivePermitCard } from './permitDetailsHeader/ReceivePermitCard'
import { RespondToBriefingCard } from './permitDetailsHeader/RespondToBriefingCard'
import { ReviewExtensionRequestPermitCard } from './permitDetailsHeader/ReviewExtensionRequestPermitCard'
import { FinalSignoffPermitCard } from './permitDetailsHeader/FinalSignoffPermitCard'
import { PermitSuspensionCard } from './permitDetailsHeader/PermitSuspensionCard'
import { PermitSuspensionReasonCard } from './permitDetailsHeader/PermitSuspensionReasonCard'
import { PostPermitChecksCard } from './permitDetailsHeader/PostPermitChecksCard'
import {
  TimelineMap,
  PolygonEastingNorthingList,
  SimpleMapModal,
} from '@modules/geolocation'
import { useRouter } from 'next/router'
import { BriefingSessionCard } from '@modules/briefings'
import { useProductAccess } from 'webapp/providers/authentication'
import { UserProfileHoverWrapper } from '@elements/UserProfileHoverWrapper'
import { CheckInStatusChip } from '@elements/CheckIn/CheckInStatusChip'
import { PermitPersonCheckInActions } from './PermitPersonCheckInActions'
import useFeatureFlags from '@core/providers/useFeatureFlags'

export const PermitDetailsSidebar: React.FC<PermitDetailsSidebarProps> = ({
  organisationId,
  projectId,
  permit,
}) => {
  const { showModal } = useModal()
  const router = useRouter()
  const { isViewMode } = useProductAccess()
  const { flags } = useFeatureFlags(['web_permit_check_in'])

  const { locationPlan, selectedAreas } =
    permit?.projectLocations?.areas?.[0] || {}
  const pins = permit?.projectLocations?.pins?.[0]

  const { locations } = permit?.projectLocations || {}

  const planId = locationPlan?.id || pins?.planId
  const locationNodeId = locations?.[0]?.locationNodeId

  const { data: plan } = useGetProjectPlan(
    {
      projectId: permit?.project?.id,
      nodeId: locationNodeId,
      planId,
    },
    {
      enabled: !!locationNodeId && !!planId && !!permit?.project?.id,
      refetchOnMount: false,
    },
  )

  const reviews: PermitApprovalWrapper[] = useMemo(() => {
    return permit?.getReviews() || []
  }, [permit])

  const permitHolderParticipant = permit.participants.find(
    ({ userId }) => userId === permit.permitHolder.id,
  )

  const otherParticipants = permit.participants.filter(
    (participant) =>
      participant.status === 'ACTIVE' &&
      participant.userId !== permit.permitAuthor?.id &&
      participant.userId !== permit.permitHolder?.id,
  )

  // Participants declared during the request phase but not yet promoted to
  // real permit participants — shown in the People section flagged "Awaiting
  // Approval".
  const declaredParticipants = permit.getUnpromotedBriefingParticipants()

  const { data: briefableRoles } = useGetPermitTemplateRolesByProject(
    { templateId: permit.template?.id, projectId: permit.project?.id },
    {
      enabled:
        !!declaredParticipants.length &&
        !!permit.template?.id &&
        !!permit.project?.id,
    },
  )
  const roleNameById = PermitTemplateRole.toNameById(briefableRoles)

  const unlistedSessions = flags.web_permit_check_in
    ? permit.getUnlistedOpenCheckInSessions()
    : []

  return (
    <Box>
      {permit && (
        <>
          <ReviewPermitCard
            organisationId={organisationId}
            projectId={projectId}
            permit={permit}
          />

          <RequestedChangesCard
            organisationId={organisationId}
            projectId={projectId}
            permit={permit}
          />

          <TransferPermitCard permit={permit} />

          <ReceivePermitCard permit={permit} projectId={projectId} />

          <RespondToBriefingCard permit={permit} />

          <ReviewExtensionRequestPermitCard permit={permit} />

          <FinalSignoffPermitCard
            permit={permit}
            organisationId={organisationId}
            projectId={projectId}
          />

          <PostPermitChecksCard
            permit={permit}
            organisationId={organisationId}
            projectId={projectId}
          />

          <PermitSuspensionCard permit={permit} />

          <PermitSuspensionReasonCard permit={permit} />

          {permit.shouldShowBriefingCard() && (
            <BriefingSessionCard
              sx={{ mb: 2 }}
              entityId={permit.id}
              entityType='PERMIT'
              briefingSession={permit.lastBriefingSession}
            />
          )}
        </>
      )}

      <Card>
        <CardContent>
          <Text
            sx={{ fontSize: '16px' }}
            variant='h6'
            color='text.primary'
            gutterBottom
          >
            {localizedStrings.generalInformation}
          </Text>
          <Divider />

          <Section label={localizedStrings.summary} mt={2}>
            <Text data-test='permit-summary' showTooltip>
              {permit?.summary}
            </Text>
          </Section>

          <Section label={localizedStrings.project} mt={2}>
            <Text
              showTooltip
              color='primary.main'
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                router.push(
                  PageUrls.project(organisationId, permit?.project?.id),
                )
              }}
            >
              {permit?.project?.name}
            </Text>
          </Section>

          <Section label={localizedStrings.location} mt={2}>
            <Text showTooltip>{permit?.location}</Text>
          </Section>

          {(permit?.projectLocations?.areas?.length > 0 ||
            permit?.projectLocations?.pins?.length > 0) && (
            <Section label={localizedStrings.zones} mt={2}>
              <Text
                color='primary.main'
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  if (
                    !permit?.projectLocations?.locations?.[0]?.locationName ||
                    !locationNodeId ||
                    !planId ||
                    !permit?.project?.id
                  )
                    return null

                  showModal(
                    <ProjectPlanPermitsModal
                      nodeId={locationNodeId}
                      projectId={permit.project.id}
                      planId={plan.id}
                      nodePath={
                        permit.projectLocations.locations[0].locationName
                      }
                      initialSelectedAreas={selectedAreas?.map(
                        (area) => area.uuid,
                      )}
                      permit={permit}
                      plan={plan}
                      readOnly
                    />,
                  )
                }}
                showTooltip
              >
                {plan?.getHumanReadableValueString(
                  selectedAreas?.map((area) => area.uuid) || [],
                  pins?.droppedPins || [],
                )}
              </Text>
            </Section>
          )}

          {permit?.getW3wLocation() && (
            <Section label={localizedStrings.pointOfWorks3Words} mt={2}>
              <Text
                color='primary.main'
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  showModal(
                    <SimpleMapModal
                      projectId={permit.project.id}
                      featurePoint={permit?.getW3wLocation()}
                      footerActions={[
                        !isViewMode && (
                          <Button
                            key={'open-all-permits'}
                            startIcon={<LaunchIcon />}
                            variant={'outlined'}
                            onClick={() => {
                              window.open(
                                PageUrls.projectMap({
                                  organisationId,
                                  projectId: permit.project.id,
                                }),
                                '_blank',
                                'noopener, noreferrer',
                              )
                            }}
                          >
                            {localizedStrings.allPermits}
                          </Button>
                        ),
                      ]}
                    />,
                  )
                }}
                showTooltip
              >
                {permit.getW3wLocation().properties.w3wLocation}
              </Text>
            </Section>
          )}

          {permit?.getGeolocationAreas() && (
            <Section label={localizedStrings.permitAreas} mt={2}>
              <TimelineMap
                projectId={permit.project.id}
                featurePolygon={permit?.getGeolocationAreas()}
                style={{
                  height: 155,
                  marginTop: 8,
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
                interactive={false}
                enableNavigationControl={false}
                hideDisclaimer={true}
                hideMapLayersList={true}
                onClick={() => {
                  showModal(
                    <SimpleMapModal
                      projectId={permit.project.id}
                      featurePolygon={permit?.getGeolocationAreas()}
                      showEastingNorthingList
                      footerActions={[
                        !isViewMode && (
                          <Button
                            key={'open-all-permits'}
                            startIcon={<LaunchIcon />}
                            variant={'outlined'}
                            onClick={() => {
                              window.open(
                                PageUrls.projectMap({
                                  organisationId,
                                  projectId: permit.project.id,
                                }),
                                '_blank',
                                'noopener, noreferrer',
                              )
                            }}
                          >
                            {localizedStrings.allPermits}
                          </Button>
                        ),
                      ]}
                    />,
                  )
                }}
              />
              <PolygonEastingNorthingList
                featurePolygon={permit?.getGeolocationAreas()}
                verticesPerLine='one'
              />
            </Section>
          )}

          <Section label={localizedStrings.permitStart} mt={2}>
            <Text showTooltip>
              {moment(permit.startsOn).format('DD MMM YYYY HH:mm')}
            </Text>
          </Section>

          <Section label={localizedStrings.permitEnd} mt={2}>
            <Text showTooltip>
              {moment(permit.endsOn).format('DD MMM YYYY HH:mm')}
            </Text>
          </Section>

          <Section label={localizedStrings.permitDuration} mt={2}>
            <Text showTooltip>{permit.getDuration()}</Text>
          </Section>

          {!!permit.getNextNominatedApprovers().length && (
            <Section
              label={localizedStrings.nextNominatedApprover}
              mt={2}
              data-test='nominated-approver-section'
            >
              <Flex flexWrap='wrap' columnGap={0.5}>
                {permit
                  .getNextNominatedApprovers()
                  .flatMap(({ targetApprovers }) => targetApprovers)
                  .map((approver, index, approvers) => (
                    <UserProfileHoverWrapper
                      key={`${approver.id}-${index}`}
                      userId={approver.id}
                      organisationId={organisationId}
                    >
                      <Text>
                        {approver.name}
                        {index < approvers.length - 1 && ','}
                      </Text>
                    </UserProfileHoverWrapper>
                  ))}
              </Flex>
            </Section>
          )}
        </CardContent>
      </Card>
      <Card sx={{ marginTop: 2 }}>
        <CardContent>
          <Text
            showTooltip
            sx={{ fontSize: '16px' }}
            variant='h6'
            color='text.primary'
            gutterBottom
          >
            {localizedStrings.people}
          </Text>
          <Divider />

          {permit?.permitAuthor && (
            <Section label={localizedStrings.initiatedBy} mt={2}>
              <Flex
                justifyContent='space-between'
                alignItems='flex-start'
                onClick={() =>
                  router.push(
                    PageUrls.organisationUserProfile({
                      organisationId,
                      userId: permit.permitAuthor.id,
                    }),
                  )
                }
                sx={{ cursor: 'pointer' }}
              >
                <Flex flexDirection='column' alignItems='flex-start'>
                  <UserProfileHoverWrapper
                    userId={permit.permitAuthor.id}
                    organisationId={organisationId}
                  >
                    <Text>{permit.permitAuthor.getFullName()}</Text>
                  </UserProfileHoverWrapper>
                  <CheckInStatusChip
                    session={permit.getCheckInSessionForUser(
                      permit.permitAuthor.id,
                    )}
                    sx={{ mt: 0.5 }}
                  />
                </Flex>
                <Flex alignItems='center' gap={1}>
                  <PermitPersonCheckInActions
                    permit={permit}
                    session={permit.getCheckInSessionForUser(
                      permit.permitAuthor.id,
                    )}
                  />
                  <ChevronRight />
                </Flex>
              </Flex>
            </Section>
          )}

          {permit?.permitHolder && (
            <Section
              label={`${localizedStrings.permitHolder} ${
                permitHolderParticipant?.roles?.length
                  ? `- ${permitHolderParticipant.getRole()}`
                  : ''
              }`}
              mt={2}
            >
              <Flex
                justifyContent='space-between'
                alignItems='flex-start'
                onClick={() =>
                  router.push(
                    PageUrls.organisationUserProfile({
                      organisationId,
                      userId: permit.permitHolder.id,
                    }),
                  )
                }
                sx={{ cursor: 'pointer' }}
              >
                <Flex flexDirection='column' alignItems='flex-start'>
                  <UserProfileHoverWrapper
                    userId={permit.permitHolder.id}
                    organisationId={organisationId}
                    lastBriefedOn={
                      permitHolderParticipant?.briefing?.lastBriefedOn
                    }
                  >
                    <Text>{permit.permitHolder.getFullName()}</Text>
                  </UserProfileHoverWrapper>
                  <CheckInStatusChip
                    session={permit.getCheckInSessionForUser(
                      permit.permitHolder.id,
                    )}
                    sx={{ mt: 0.5 }}
                  />
                </Flex>
                <Flex alignItems='center' gap={1}>
                  <PermitPersonCheckInActions
                    permit={permit}
                    session={permit.getCheckInSessionForUser(
                      permit.permitHolder.id,
                    )}
                  />
                  <ChevronRight />
                </Flex>
              </Flex>
            </Section>
          )}

          {!!reviews.length &&
            reviews.map((approval) => {
              return (
                <Section
                  key={approval.submittedBy.id}
                  label={approval.getSectionHeading()}
                  mt={2}
                >
                  <Flex
                    justifyContent='space-between'
                    alignItems='flex-start'
                    onClick={() =>
                      router.push(
                        PageUrls.organisationUserProfile({
                          organisationId,
                          userId: approval.submittedBy.id,
                        }),
                      )
                    }
                    sx={{ cursor: 'pointer' }}
                  >
                    <Flex flexDirection='column' alignItems='flex-start'>
                      <UserProfileHoverWrapper
                        userId={approval.submittedBy.id}
                        organisationId={organisationId}
                      >
                        <Text>{approval.submittedBy.getFullName()}</Text>
                      </UserProfileHoverWrapper>
                      <CheckInStatusChip
                        session={permit.getCheckInSessionForUser(
                          approval.submittedBy.id,
                        )}
                        sx={{ mt: 0.5 }}
                      />
                    </Flex>
                    <Flex alignItems='center' gap={1}>
                      <PermitPersonCheckInActions
                        permit={permit}
                        session={permit.getCheckInSessionForUser(
                          approval.submittedBy.id,
                        )}
                      />
                      <ChevronRight />
                    </Flex>
                  </Flex>
                </Section>
              )
            })}

          {(otherParticipants.length > 0 ||
            declaredParticipants.length > 0 ||
            unlistedSessions.length > 0) && (
            <Collapsible
              label={{
                collapsed: localizedStrings.showAll,
                open: localizedStrings.hideAll,
              }}
              pt={4}
            >
              <Text
                sx={{ fontSize: '16px' }}
                variant='h6'
                color='text.primary'
                gutterBottom
                showTooltip
              >
                {localizedStrings.otherParticipants}
              </Text>
              <Divider sx={{ mb: 1 }} />

              {otherParticipants.map((participant) => (
                <Section
                  key={participant.id}
                  label={participant.getRole()}
                  mt={2}
                >
                  <Flex
                    justifyContent='space-between'
                    alignItems='flex-start'
                    onClick={() =>
                      participant.isSiteAssistUser() &&
                      router.push(
                        PageUrls.organisationUserProfile({
                          organisationId,
                          userId: participant.userId,
                        }),
                      )
                    }
                    sx={{ cursor: 'pointer' }}
                  >
                    <Flex flexDirection='column' alignItems='flex-start'>
                      <UserProfileHoverWrapper
                        userId={participant.userId}
                        externalUser={participant}
                        organisationId={organisationId}
                        // Session briefings write no per-participant audit, so fall back to the briefing signature time
                        lastBriefedOn={
                          participant.briefing?.lastBriefedOn ??
                          participant.lastSignedOn
                        }
                      >
                        <Text>{participant.getFullName()}</Text>
                      </UserProfileHoverWrapper>
                      <CheckInStatusChip
                        session={permit.getCheckInSessionForUser(
                          participant.userId,
                        )}
                        sx={{ mt: 0.5 }}
                      />
                    </Flex>
                    <Flex alignItems='center' gap={1}>
                      {participant.hasPendingAcceptance() && (
                        <Chip
                          label={localizedStrings.pendingAcceptance}
                          size='small'
                          color='warning'
                        />
                      )}
                      <PermitPersonCheckInActions
                        permit={permit}
                        session={permit.getCheckInSessionForUser(
                          participant.userId,
                        )}
                      />
                      <ChevronRight />
                    </Flex>
                  </Flex>
                </Section>
              ))}

              {declaredParticipants.map((participant) => (
                <Section
                  key={participant.sessionParticipantId}
                  label={
                    roleNameById[participant.briefOnRoleId] ??
                    localizedStrings.role
                  }
                  mt={2}
                >
                  <Flex justifyContent='space-between' alignItems='center'>
                    <UserProfileHoverWrapper
                      userId={participant.userId}
                      externalUser={participant}
                      organisationId={organisationId}
                    >
                      <Text>
                        {`${participant.firstName} ${participant.lastName}`}
                      </Text>
                    </UserProfileHoverWrapper>
                    <Chip
                      label={statuses.approval.label}
                      size='small'
                      color={statuses.approval.color}
                      variant='outlined'
                    />
                  </Flex>
                </Section>
              ))}

              {unlistedSessions.length > 0 && (
                <>
                  <Text
                    sx={{ fontSize: '16px' }}
                    variant='h6'
                    color='text.primary'
                    mt={3}
                    gutterBottom
                  >
                    {localizedStrings.alsoOnSite}
                  </Text>
                  <Divider sx={{ mb: 1 }} />
                  {unlistedSessions.map((session) => (
                    <Section key={session.id} mt={2}>
                      <Flex
                        justifyContent='space-between'
                        alignItems='flex-start'
                      >
                        <Flex flexDirection='column' alignItems='flex-start'>
                          <UserProfileHoverWrapper
                            userId={session.user.id}
                            organisationId={organisationId}
                          >
                            <Text>{session.user.getFullName()}</Text>
                          </UserProfileHoverWrapper>
                          <CheckInStatusChip
                            session={session}
                            sx={{ mt: 0.5 }}
                          />
                        </Flex>
                        <Flex alignItems='center' gap={1}>
                          <PermitPersonCheckInActions
                            permit={permit}
                            session={session}
                          />
                        </Flex>
                      </Flex>
                    </Section>
                  ))}
                </>
              )}
            </Collapsible>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
